import { Form, Formik, FormikHelpers } from 'formik';
import React, { useState } from 'react';
import { date, object, string } from 'yup';
import { isFirstDayOfMonth, startOfDay } from 'date-fns';
import { AxiosError } from 'axios';
import Personnummer from 'personnummer';

// Components
import {
  FormikDropdown,
  FormikInput,
  FormikSummary,
  FormikTextArea,
} from '../../components/formik';
import FormikDatePicker from '../../components/formik/FormikDatePicker';
import { Button } from '../../components/Button';

// Utils
import { isPersonalNumberValid } from '../../utilities/person';

// Context
import { useGlobalState } from '../../context/global-context/hooks';

// Services
import { createTradeUnionCase } from '../../services/formService';

// Models
import { TradeUnionCaseRequest } from '../../models/Requests/TradeUnionCaseRequest';
import { FormikDropdownOption, FormikFormStatus } from '../../models/Formik';

// i18n
import { useTranslation } from '../../translations/useTranslation';

interface FormValue {
  ssn: string;
  name: string;
  email: string;
  phone: string;
  reason: string;
  choice: string;
  date: Date | null;
}

const dateFilterFn = (date: Date) => isFirstDayOfMonth(date);

const TradeUnionPaymentFormPage = () => {
  const { state } = useGlobalState();
  const { t, keys } = useTranslation();

  // State used to show loading button when redirecting to thank you page
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

  const paymentOptions: FormikDropdownOption[] = [
    {
      label: t(keys.tradeUnionFormPaymentType1), // Direktanslutning
      value: '1',
    },
    {
      label: t(keys.tradeUnionFormPaymentType2), // Till förbund (ta över fakturering)
      value: '2',
    },
  ];

  const initialValues: FormValue = {
    ssn: '',
    name: '',
    email: '',
    phone: '',
    reason: '',
    choice: '',
    date: null,
  };

  const validationSchema = object().shape({
    ssn: string()
      .required(t(keys.errorRequired))
      .test('personnummer-test', t(keys.errorInvalidPersonalID), (value) =>
        isPersonalNumberValid(value)
      ),
    name: string().required(t(keys.errorRequired)),
    choice: string().required(t(keys.errorOneOfPayments)),
    email: string().email(t(keys.errorInvalidEmail)),
    date: date().nullable().required(t(keys.errorRequired)),
  });

  const handleSubmit = async (
    values: FormValue,
    helpers: FormikHelpers<FormValue>
  ) => {
    try {
      setIsRedirecting(false);
      helpers.setStatus({
        state: 'NONE',
      });
      const ssn = new Personnummer(values.ssn);
      const request: TradeUnionCaseRequest = {
        socialSecurityNumber: ssn.format(true), // Convert to long format
        name: values.name,
        email: values.email,
        phone: values.phone,
        reason: values.reason,
        paymentViaTradeUnion: values.choice === '2',
        date: values.date,
        umbracoPageId: state.pageId,
      };
      const response = await createTradeUnionCase(request);
      if (response.data.ThankYouPageUrl) {
        setIsRedirecting(true);
        location.href = response.data.ThankYouPageUrl;
      } else {
        // In case thank you page is not specified, display generic success message
        helpers.resetForm();
        const status: FormikFormStatus = {
          state: 'SUCCESS',
          message: t(keys.tradeUnionFormSuccessMessage),
        };
        helpers.setStatus(status);
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const status: FormikFormStatus = {
        state: 'ERROR',
      };
      switch (axiosError.response?.status) {
        case 400:
          status.message = t(keys.tradeUnionFormError400);
          break;
        case 403: // 401 really, but Umbraco catches 401 responses
          status.message = t(keys.tradeUnionFormError401);
          location.reload();
          break;
        case 404:
          status.message = t(keys.tradeUnionFormError404);
          break;
        case 500:
        default:
          status.message = t(keys.tradeUnionFormError500);
          break;
      }
      helpers.setStatus(status);
      helpers.setSubmitting(false);
    }
  };

  return (
    <div className="form-app__trade-union-form">
      <h2 className="form-app__heading">{t(keys.tradeUnionFormHeading)}</h2>
      <Formik
        validateOnMount
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, isValid, status, submitForm }) => (
          <Form>
            <FormikInput
              label={`${t(keys.uiPersonalIdNumber)} *`}
              type="text"
              name="ssn"
            />
            <FormikInput
              label={`${t(keys.uiName)} *`}
              type="text"
              name="name"
            />
            <FormikDropdown
              name="choice"
              label={`${t(keys.tradeUnionFormPaymentType)} *`}
              placeholder={t(keys.tradeUnionFormSelectPaymentType)}
              options={paymentOptions}
            />
            <FormikDatePicker
              name="date"
              label={`${t(keys.tradeUnionFormDate)} *`}
              locale={state.locale}
              dateFilter={dateFilterFn}
            />
            <FormikInput
              label={t(keys.tradeUnionFormMemberEmail)}
              type="email"
              name="email"
            />
            <FormikInput
              label={t(keys.tradeUnionFormMemberPhone)}
              type="text"
              name="phone"
            />
            <FormikTextArea
              label={t(keys.tradeUnionFormReason)}
              rows={10}
              maxLength={2000}
              name="reason"
            />
            <Button
              title={t(keys.uiSubmit)}
              disabled={!isValid}
              onClick={submitForm}
              isLoading={isSubmitting || isRedirecting}
            />
            {status && status.state !== 'NONE' && <FormikSummary {...status} />}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default TradeUnionPaymentFormPage;
